// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";

interface OwnProps {
    isQtyAdjusted: boolean;
    multipleProducts: boolean;
    // XNG-Changes
    qtyExceededInventory: boolean;
    isNonStock?: boolean;
    defaultMessageOverride?: string;
    isProductEvent?: boolean;
}

type Props = OwnProps;

export interface ProductAddedToCartMessageStyles {
    messageWrapper?: InjectableCss;
    defaultText?: TypographyPresentationProps;
    quantityAdjustedText?: TypographyPresentationProps;
    // XNG-Changes
    warning?: TypographyPresentationProps;
    title?: TypographyPresentationProps;
}

export const productAddedToCartMessageStyles: ProductAddedToCartMessageStyles = {
    defaultText: {
        variant: "p",
    },
    quantityAdjustedText: {
        variant: "p",
    },
};

const styles = productAddedToCartMessageStyles;

const Message = ({ defaultMessage, warningMessage }: { defaultMessage: any; warningMessage?: any }) => {
    return (
        <StyledWrapper {...styles.messageWrapper}>
            <Typography {...styles.title}>{translate("Add To Cart")}</Typography>
            <Typography {...styles.defaultText}>{defaultMessage}</Typography>
            {warningMessage && <Typography {...styles.warning}>{warningMessage}</Typography>}
        </StyledWrapper>
    );
};

const ProductAddedToCartMessage = ({
    isQtyAdjusted,
    multipleProducts,
    qtyExceededInventory,
    isNonStock,
    defaultMessageOverride,
    isProductEvent,
}: Props) => {
    const defaultMessage =
        defaultMessageOverride || multipleProducts
            ? siteMessage("Cart_AllProductsAddedToCart")
            : siteMessage("Cart_ProductAddedToCart");

    // XNG-Changes
    const eventProductMessage = siteMessage("Cart_EventAddToCart");

    if (isProductEvent) {
        return <Message defaultMessage={eventProductMessage} />;
    }

    if (isNonStock) {
        return (
            <Message
                defaultMessage={defaultMessage}
                warningMessage={siteMessage("Werner_Cart_ItemWillBeBackOrdered")}
            />
        );
    }
    if (qtyExceededInventory) {
        return (
            <Message
                defaultMessage={defaultMessage}
                warningMessage={siteMessage("Werner_Cart_SomeItemsWillBeBackOrdered")}
            />
        );
    }

    if (!isQtyAdjusted) {
        return <Message defaultMessage={defaultMessage} />;
    }

    return <Message defaultMessage={defaultMessage} warningMessage={siteMessage("Cart_QuantityAdjusted")} />;
};

export default ProductAddedToCartMessage;
